import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Logo from '../components/Logo'
import { getAllClubs, ALL_COUNTIES } from '../clubData'
import {
  buildBaseSquadSlug, findCollidingSquad, appendLabelToSlug, appendLabelToName, areNamesIndistinguishable,
  findAvailableSlug, notifySquadRelabeled, RETROACTIVE_LABEL, COLLISION_LABEL_PLACEHOLDERS
} from '../utils/squadPublicUrl'

const ALL_GAA_CLUBS = getAllClubs()
const DUBLIN_GAA_CLUBS = ALL_GAA_CLUBS.map(c => c.name)

const AGE_GROUPS = ['Senior', 'U20', 'Minor', 'U16', 'U15', 'U14', 'U13', 'U12', 'U11', 'U10']

function getSportLabel(sport) {
  if (sport === 'football') return 'Football'
  if (sport === 'hurling') return 'Hurling'
  if (sport === 'camogie') return 'Camogie'
  if (sport === 'dual') return 'Dual (Football & Hurling)'
  if (sport === 'dual_ladies') return 'Dual (Football & Camogie)'
  return sport
}

export default function Login() {
  const [mode, setMode] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('invite')) return 'signup'
    if (params.get('reset')) return 'reset'
    if (params.get('mode') === 'signup') return 'signup'
    return 'signin'
  })
  const [step, setStep] = useState(1)

  const [email, setEmail] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('invite') || ''
  })
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')

  const [clubSearch, setClubSearch] = useState('')
  const [clubName, setClubName] = useState('')
  const [showClubPicker, setShowClubPicker] = useState(false)
  const [clubCounty, setClubCounty] = useState('')
  const [countySearch, setCountySearch] = useState('')
  const [clubId, setClubId] = useState(null)
  const [isNewClub, setIsNewClub] = useState(false)
  const [existingSquads, setExistingSquads] = useState([])
  const [loadingSquads, setLoadingSquads] = useState(false)

  const [squadMode, setSquadMode] = useState(null)
  const [selectedSquadId, setSelectedSquadId] = useState('')
  const [newSquadGender, setNewSquadGender] = useState('boys')
  const [newSquadAgeGroup, setNewSquadAgeGroup] = useState('')
  const [newSquadSport, setNewSquadSport] = useState('football')
  const [collisionSquad, setCollisionSquad] = useState(null)
  const [publicUrlLabel, setPublicUrlLabel] = useState('')
  const [labelPlaceholder] = useState(() => COLLISION_LABEL_PLACEHOLDERS[Math.floor(Math.random() * COLLISION_LABEL_PLACEHOLDERS.length)])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showConfirmClub, setShowConfirmClub] = useState(false)

  const isInvited = !!new URLSearchParams(window.location.search).get('invite')
  const filteredClubs = ALL_GAA_CLUBS.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(clubSearch.toLowerCase())
    const matchesCounty = !countySearch || c.county === countySearch
    return matchesSearch && matchesCounty
  })

  useEffect(() => {
    if (squadMode !== 'create' || !newSquadAgeGroup || !clubId) { setCollisionSquad(null); setPublicUrlLabel(''); return }
    let cancelled = false
    async function checkCollision() {
      const { data: squads } = await supabase.from('squads').select('*').eq('club_id', clubId)
      if (cancelled) return
      const candidate = { current_grade: newSquadAgeGroup, gender: newSquadGender, sport: newSquadSport }
      const match = findCollidingSquad(squads || [], candidate)
      setCollisionSquad(match)
      if (!match) setPublicUrlLabel('')
    }
    checkCollision()
    return () => { cancelled = true }
  }, [squadMode, newSquadAgeGroup, newSquadGender, newSquadSport, clubId])

  async function handleSignUpStep1() {
    if (!fullName.trim()) { setError('Please enter your full name.'); return }
    if (!email.trim()) { setError('Please enter your email.'); return }
    if (!password || password.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return }
    setError('')
    if (isInvited) {
      // Skip club/squad steps — use pending invite data
      setLoading(true)
      try {
        const inviteEmail = new URLSearchParams(window.location.search).get('invite') || email.trim().toLowerCase()
        const { data: pendingInvite } = await supabase
          .from('pending_invites').select('*')
          .eq('email', inviteEmail.trim().toLowerCase()).maybeSingle()
        if (!pendingInvite) { setError('No invite found for this email address.'); setLoading(false); return }

        const { error: authError } = await supabase.auth.signUp({
          email: email.trim().toLowerCase(),
          password,
          options: {
            emailRedirectTo: 'https://gaamatchtracker.ie',
            data: {
              full_name: fullName.trim(),
              club_id: pendingInvite.club_id,
              club_name: '',
              role: pendingInvite.role || 'coach',
              approved: true
            }
          }
        })
        if (authError) { setError(authError.message); setLoading(false); return }

        const { data: { user } } = await supabase.auth.getUser()
        const userId = user?.id

        if (pendingInvite.squad_id && userId) {
          await supabase.rpc('create_squad_member_on_signup', {
            user_id: userId,
            user_squad_id: pendingInvite.squad_id,
            user_role: pendingInvite.role || 'coach',
            user_approved: true
          })
        }

        await supabase.from('pending_invites').delete().eq('id', pendingInvite.id)
        await supabase.auth.signOut()
        window.history.replaceState({}, '', '/')
        setMessage('Account created! You can now sign in.')
        setMode('signin'); setStep(1)
      } catch (e) {
        setError('Error: ' + e.message)
      }
      setLoading(false)
      return
    }
    setStep(2)
  }

  async function handleSignUpStep2Confirm() {
    if (!clubName.trim()) { setError('Please enter or select your club name.'); return }
    if (!ALL_GAA_CLUBS.find(c => c.name.toLowerCase() === clubName.trim().toLowerCase())) {
      // Not in the built-in list: only ask "create this club?" if it isn't in the database either
      const { data: existing } = await supabase.from('clubs').select('id').ilike('club_name', clubName.trim()).limit(1)
      if (!existing || existing.length === 0) {
        setShowConfirmClub(true)
        return
      }
    }
    await handleSignUpStep2()
  }

  async function handleSignUpStep2() {
    if (!clubName.trim()) { setError('Please enter or select your club name.'); return }
    setLoadingSquads(true); setError('')

    const { data: existingClubs } = await supabase
      .from('clubs').select('id').ilike('club_name', clubName.trim())
    const existingClub = existingClubs && existingClubs.length > 0 ? existingClubs[0] : null

    if (!existingClub) {
      // No club exists — create club and make them Club Admin
      setLoadingSquads(false)
      await handleCreateNewClub()
      return
    }

    setClubId(existingClub.id)
    const { data: squads } = await supabase
      .from('squads').select('*').eq('club_id', existingClub.id).order('name')
    setExistingSquads(squads || [])
    setLoadingSquads(false)
    if (!squads || squads.length === 0) {
      setSquadMode('create')
    } else {
      setSquadMode(null)
    }
    setStep(3)
  }

  async function handleCreateNewClub() {
    setLoading(true); setError('')
    try {
      // Create the club first
      const baseSlug = clubName.trim().toLowerCase()
        .replace(/'/g, '').replace(/[áàâä]/g, 'a').replace(/[éèêë]/g, 'e')
        .replace(/[íìîï]/g, 'i').replace(/[óòôö]/g, 'o').replace(/[úùûü]/g, 'u')
        .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-')
      const countySlug = clubCounty ? '-' + clubCounty.trim().toLowerCase().replace(/\s+/g, '-') : ''
      const baseClubSlug = baseSlug + countySlug
      // Check for uniqueness and append number if needed
      let clubSlug = baseClubSlug
      let slugSuffix = 2
      while (true) {
        const { data: existing } = await supabase.from('clubs').select('id').eq('slug', clubSlug).maybeSingle()
        if (!existing) break
        clubSlug = baseClubSlug + '-' + slugSuffix
        slugSuffix++
      }
      const { data: newClub, error: clubError } = await supabase
        .from('clubs').insert({ club_name: clubName.trim(), name: clubName.trim(), county: clubCounty || null, slug: clubSlug })
        .select().single()
      if (clubError) { setError('Error creating club: ' + clubError.message); setLoading(false); return }

      // Don't sign up yet — carry the user into step 3 to create their squad first.
      // handleSignUpStep3 does the actual auth.signUp once the squad is chosen.
      setClubId(newClub.id)
      setExistingSquads([])
      setSquadMode('create')
      setIsNewClub(true)
      setStep(3)
    } catch (e) {
      setError('Error: ' + e.message)
    }
    setLoading(false)
  }

  async function handleSignUpStep3() {
    if (squadMode === 'join' && !selectedSquadId) { setError('Please select a squad.'); return }
    if (squadMode === 'create' && !newSquadAgeGroup) { setError('Please select an age group.'); return }
    if (squadMode === 'create' && collisionSquad && !publicUrlLabel.trim()) {
      setError("Please tell us what's different about this squad.")
      return
    }
    setLoading(true); setError('')
    try {
      // Check for pending invite
      const { data: pendingInvite } = await supabase
        .from('pending_invites').select('*')
        .eq('email', email.trim().toLowerCase()).maybeSingle()

      let squadId = selectedSquadId
      let role = 'coach'
      let approved = false

      if (pendingInvite) {
        squadId = pendingInvite.squad_id
        role = pendingInvite.role || 'coach'
        approved = true
      } else if (squadMode === 'create') {
        // Create new squad — public URL is generated once here from permanent
        // attributes (birth-year cohort or grade name) and never touched again
        // by later age-group edits.
        const { data: clubRow } = await supabase.from('clubs').select('slug').eq('id', clubId).single()
        const clubSlug = clubRow?.slug
        const seasonYear = new Date().getFullYear()
        const baseSlug = buildBaseSquadSlug({ clubSlug, gradeText: newSquadAgeGroup, gender: newSquadGender, sport: newSquadSport, seasonYear })
        const birthYearMatch = /^U(\d+)$/i.exec(newSquadAgeGroup.trim())
        const birthYear = birthYearMatch && !['20', '21'].includes(birthYearMatch[1]) ? seasonYear - Number(birthYearMatch[1]) : null

        const baseSquadName = newSquadAgeGroup + ' ' + (newSquadGender === 'boys' ? 'Boys' : 'Ladies')
        let squadName = baseSquadName
        let publicUrlLabelValue = null
        let finalSlug = baseSlug
        if (collisionSquad) {
          publicUrlLabelValue = publicUrlLabel.trim()
          squadName = appendLabelToName(squadName, publicUrlLabelValue)
          finalSlug = appendLabelToSlug(baseSlug, publicUrlLabelValue)
        }
        finalSlug = await findAvailableSlug(supabase, finalSlug)

        const { data: newSquad, error: squadError } = await supabase.from('squads').insert({
          name: squadName, sport: newSquadSport,
          age_group: newSquadAgeGroup, gender: newSquadGender,
          current_grade: newSquadAgeGroup,
          club_id: clubId,
          birth_year: birthYear,
          public_url_label: publicUrlLabelValue,
          slug: finalSlug
        }).select().single()
        if (squadError) { setError('Error creating squad: ' + squadError.message); setLoading(false); return }
        squadId = newSquad.id
        role = isNewClub ? 'club_admin' : 'squad_admin'
        approved = true

        // Retroactively give the colliding squad a distinguishing label/URL
        // too, but only if its name doesn't already read as distinct on its
        // own (e.g. "Simonstown U12 Premier" doesn't need "- 1sts" tacked on
        // just because a generic "U12 Boys" squad was created elsewhere).
        if (collisionSquad && !collisionSquad.public_url_label && areNamesIndistinguishable(baseSquadName, collisionSquad.name)) {
          const existingBaseSlug = buildBaseSquadSlug({
            clubSlug,
            gradeText: collisionSquad.current_grade || collisionSquad.age_group,
            gender: collisionSquad.gender, sport: collisionSquad.sport, seasonYear
          })
          const existingName = appendLabelToName(collisionSquad.name, RETROACTIVE_LABEL)
          const existingSlug = await findAvailableSlug(supabase, appendLabelToSlug(existingBaseSlug, RETROACTIVE_LABEL))
          await supabase.from('squads').update({
            name: existingName, slug: existingSlug, public_url_label: RETROACTIVE_LABEL
          }).eq('id', collisionSquad.id)
          await notifySquadRelabeled(supabase, {
            squad: collisionSquad, newName: existingName, newSlug: existingSlug, clubName: clubName.trim()
          })
        }

        // Email Club Admin
        const { data: clubAdmins } = await supabase
          .from('profiles').select('*').eq('club_id', clubId).eq('role', 'club_admin')
        if (clubAdmins && clubAdmins.length > 0) {
          const adminEmails = clubAdmins.map(a => a.email).filter(Boolean)
          if (adminEmails.length > 0) {
            const RESEND_API_KEY = process.env.REACT_APP_RESEND_API_KEY
            await fetch('https://api.resend.com/emails', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + RESEND_API_KEY },
              body: JSON.stringify({
                from: 'GAA Match Tracker <noreply@gaamatchtracker.ie>',
                to: adminEmails,
                subject: 'New squad created at ' + clubName.trim(),
                html: '<div style="font-family: sans-serif; max-width: 480px; margin: 0 auto; padding: 24px;">'
                  + '<h2 style="color: #185FA5;">New Squad Created</h2>'
                  + '<p><strong>' + fullName.trim() + '</strong> (' + email.trim() + ') has created a new squad at ' + clubName.trim() + ':</p>'
                  + '<p style="background: #f8f8f8; padding: 12px; border-radius: 8px;"><strong>' + squadName + '</strong></p>'
                  + '<p>They have been assigned as Squad Admin. Visit the Admin panel to review.</p>'
                  + '<a href="https://gaamatchtracker.ie" style="background: #185FA5; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; display: inline-block; margin-top: 16px;">Open App</a>'
                  + '</div>'
              })
            })
          }
        }
      }

      // Sign up — trigger creates profile automatically from metadata
      const { error: authError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: 'https://gaamatchtracker.ie',
          data: {
            full_name: fullName.trim(),
            club_id: clubId,
            club_name: clubName.trim(),
            role: role,
            approved: approved
          }
        }
      })
      if (authError) { setError(authError.message); setLoading(false); return }

      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id
      if (isNewClub && userId) {
        await supabase.from('clubs').update({ created_by: userId }).eq('id', clubId)
      }
      // Sync this signup into club_contacts (matched by email) so site admin
      // has a single up-to-date contact list, including non-users added manually.
      if (userId) {
        const signupEmail = email.trim().toLowerCase()
        const { data: existingContact } = await supabase
          .from('club_contacts').select('*').eq('contact_email', signupEmail).maybeSingle()
        const contactFields = {
          club_name: clubName.trim(),
          county: clubCounty || (existingContact?.county ?? null),
          contact_name: existingContact?.contact_name || fullName.trim(),
          contact_email: signupEmail,
          club_id: clubId,
          profile_id: userId,
          signed_up: true
        }
        if (existingContact) {
          const updateFields = {}
          Object.keys(contactFields).forEach(key => {
            if (existingContact[key] === null || existingContact[key] === '' || key === 'club_id' || key === 'profile_id' || key === 'signed_up') {
              updateFields[key] = contactFields[key]
            }
          })
          updateFields.status = 'signed_up'
          await supabase.from('club_contacts').update(updateFields).eq('id', existingContact.id)
        } else {
          await supabase.from('club_contacts').insert({ ...contactFields, status: 'signed_up' })
        }
      }
      // Create squad membership
      if (squadId && userId) {
        await supabase.rpc('create_squad_member_on_signup', {
          user_id: userId,
          user_squad_id: squadId,
          user_role: role,
          user_approved: approved
        })
      }

      // Tell the club admins about the new squad (sent from the server).
      // A failure here must never stop the signup.
      if (squadMode === 'create' && !isNewClub && squadId && userId) {
        try {
          await supabase.functions.invoke('notify-squad-created', { body: { squadId } })
        } catch (e) { /* ignore */ }
      }

      // Tell the squad's admins that someone is waiting for approval (sent from the server).
      // A failure here must never stop the signup.
      if (squadMode === 'join' && !approved && squadId && userId) {
        try {
          await supabase.functions.invoke('notify-join-request', { body: { squadId } })
        } catch (e) { /* ignore */ }
      }

      if (pendingInvite) {
        await supabase.from('pending_invites').delete().eq('id', pendingInvite.id)
      }

      // Stay signed in and reload, so the app opens with everything in place:
      // approved people go straight in, and people waiting see who can approve them.
      window.location.href = '/'
      return
    } catch (e) {
      setError('Error: ' + e.message)
    }
    setLoading(false)
  }
async function handleForgotPassword() {
    if (!email.trim()) { setError('Please enter your email address.'); return }
    setLoading(true); setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: 'https://gaamatchtracker.ie?reset=true'
    })
    if (error) { setError(error.message); setLoading(false); return }
    setMessage('Password reset email sent! Check your inbox.')
    setMode('signin')
    setLoading(false)
  }

  async function handlePasswordUpdate() {
    if (!password || password.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return }
    setLoading(true); setError('')
    const { error } = await supabase.auth.updateUser({ password })
    if (error) { setError(error.message); setLoading(false); return }
    await supabase.auth.signOut()
    window.history.replaceState({}, '', '/')
    window.location.reload()
  }

  async function handleSignIn() {
    if (!email || !password) { setError('Please enter your email and password.'); return }
    setLoading(true); setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(false)
  }
if (mode === 'forgot') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔑</div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Reset Password</h2>
            <p style={{ fontSize: '14px', color: '#888', lineHeight: '1.5' }}>
              Enter your email and we'll send you a reset link.
            </p>
          </div>
          {error && <p style={{ color: '#A32D2D', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>{error}</p>}
          {message && <p style={{ color: '#3B6D11', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>{message}</p>}
          <input style={inputStyle} type="email" placeholder="Email address"
            value={email} onChange={e => setEmail(e.target.value)} />
          <button style={primaryBtn} onClick={handleForgotPassword} disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button style={linkBtn} onClick={() => { setMode('signin'); setError(''); setMessage('') }}>
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (mode === 'reset') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔒</div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Set New Password</h2>
            <p style={{ fontSize: '14px', color: '#888', lineHeight: '1.5' }}>
              Enter your new password below.
            </p>
          </div>
          {error && <p style={{ color: '#A32D2D', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>{error}</p>}
          <input style={inputStyle} type="password" placeholder="New password (min 6 characters)"
            value={password} onChange={e => setPassword(e.target.value)} />
          <input style={inputStyle} type="password" placeholder="Confirm new password"
            value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
          <button style={primaryBtn} onClick={handlePasswordUpdate} disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    )
  }

  if (mode === 'pending') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Awaiting Approval</h2>
            <p style={{ fontSize: '14px', color: '#888', lineHeight: '1.5' }}>
              Your request to join <strong>{clubName}</strong> has been sent to the admin.
              You'll be able to log in once they approve your account.
            </p>
          </div>
          <button style={primaryBtn} onClick={() => { setMode('signin'); setStep(1) }}>
            Back to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={shell}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <Logo width="100%" maxWidth="100%" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
            {mode === 'signin' ? 'Sign in to your account'
              : step === 1 ? 'Create your account'
              : step === 2 ? 'Select your club'
              : 'Select your squad'}
          </p>
          {isInvited && mode === 'signup' && (
            <div style={{ background: '#EAF3DE', borderRadius: '8px', padding: '8px 12px', marginTop: '10px', fontSize: '12px', color: '#3B6D11' }}>
              ✅ You've been invited — your account will be approved automatically
            </div>
          )}
        </div>

        {error && (
          <div style={{ background: '#FCEBEB', border: '0.5px solid #A32D2D', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#A32D2D' }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ background: '#EAF3DE', border: '0.5px solid #3B6D11', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#3B6D11' }}>
            {message}
          </div>
        )}

        {/* SIGN IN */}
        {mode === 'signin' && (
          <>
            <input style={inputStyle} type="email" placeholder="Email address"
              value={email} onChange={e => setEmail(e.target.value)} />
            <input style={inputStyle} type="password" placeholder="Password"
              value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSignIn()} />
            <button style={primaryBtn} onClick={handleSignIn} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button style={linkBtn} onClick={() => { setMode('signup'); setStep(1); setError(''); setMessage('') }}>
                Don't have an account? Sign up
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <button style={linkBtn} onClick={() => { setMode('forgot'); setError(''); setMessage('') }}>
                Forgot password?
              </button>
            </div>
          </>
        )}

        {/* STEP 1 */}
        {mode === 'signup' && step === 1 && (
          <>
            <StepIndicator current={1} total={3} />
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '14px' }}>Your details</p>
            <input style={inputStyle} type="text" placeholder="Full name *"
              value={fullName} onChange={e => setFullName(e.target.value)} />
            <input style={inputStyle} type="email" placeholder="Email address *"
              value={email} onChange={e => setEmail(e.target.value)}
              readOnly={isInvited} />
            <input style={inputStyle} type="password" placeholder="Password (min 6 characters) *"
              value={password} onChange={e => setPassword(e.target.value)} />
            <input style={inputStyle} type="password" placeholder="Confirm password *"
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            <button style={primaryBtn} onClick={handleSignUpStep1} disabled={loading}>Next →</button>
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button style={linkBtn} onClick={() => { setMode('signin'); setError(''); setMessage('') }}>
                Already have an account? Sign in
              </button>
            </div>
          </>
        )}

        {/* STEP 2 */}
        {mode === 'signup' && step === 2 && (
          <>
            <StepIndicator current={2} total={3} />
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '14px' }}>Select or enter your club</p>
            <select style={{ ...inputStyle, marginBottom: '10px' }}
              value={countySearch} onChange={e => { setCountySearch(e.target.value); setClubCounty(e.target.value) }}>
              <option value="">Select county first (optional)</option>
              {ALL_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <input style={{ ...inputStyle, marginBottom: 0 }}
                type="text" placeholder="Search or type club name *"
                value={clubName || clubSearch}
                onChange={e => { setClubSearch(e.target.value); setClubName(e.target.value); setShowClubPicker(true) }}
                onFocus={() => setShowClubPicker(true)}
              />
              {showClubPicker && clubSearch.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 50, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '4px' }}>
                  {filteredClubs.slice(0, 15).map(c => (
                    <div key={c.name + c.county} onClick={() => { setClubName(c.name); setClubSearch(c.name); setClubCounty(c.county); setCountySearch(c.county); setShowClubPicker(false) }}
                      style={{ padding: '10px 12px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{c.name}</span>
                      <span style={{ fontSize: '11px', color: '#aaa' }}>{c.county}</span>
                    </div>
                  ))}
                  {clubName && !ALL_GAA_CLUBS.find(c => c.name === clubName) && (
                    <div onClick={() => setShowClubPicker(false)}
                      style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '13px', color: '#185FA5', fontStyle: 'italic', borderTop: '0.5px solid #e0e0e0' }}>
                      Use "{clubName}" as my club name
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#888' }}>
              💡 If your club is already registered you'll be asked to select or create a squad. If you're the first from your club you'll become Club Admin.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...primaryBtn, background: '#888', flex: '0 0 80px' }}
                onClick={() => { setStep(1); setError('') }}>← Back</button>
              <button style={{ ...primaryBtn, flex: 1 }} onClick={handleSignUpStep2Confirm} disabled={loading || loadingSquads}>
                {loadingSquads ? 'Loading...' : 'Next →'}
              </button>
            </div>
            {showConfirmClub && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Create new club?</h3>
                  <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '20px' }}>
                    <strong>"{clubName}"</strong> isn't in our list. Do you want to create it as a new club? You'll become the Club Admin.
                  </p>
                  <p style={{ fontSize: '12px', color: '#888', marginBottom: '20px' }}>
                    If you made a typo, tap Cancel and search again.
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={{ ...primaryBtn, background: '#888', flex: 1 }}
                      onClick={() => setShowConfirmClub(false)}>
                      Cancel
                    </button>
                    <button style={{ ...primaryBtn, flex: 1 }}
                      onClick={async () => { setShowConfirmClub(false); await handleSignUpStep2() }}>
                      Yes, create it
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP 3 */}
        {mode === 'signup' && step === 3 && (
          <>
            <StepIndicator current={3} total={3} />
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '14px' }}>Select or create a squad</p>

            {existingSquads.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  onClick={() => { setSquadMode('join'); setError('') }}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid ' + (squadMode === 'join' ? '#185FA5' : '#e0e0e0'), background: squadMode === 'join' ? '#E6F1FB' : 'white', color: squadMode === 'join' ? '#185FA5' : '#888', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  Join existing squad
                </button>
                <button
                  onClick={() => { setSquadMode('create'); setError('') }}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid ' + (squadMode === 'create' ? '#185FA5' : '#e0e0e0'), background: squadMode === 'create' ? '#E6F1FB' : 'white', color: squadMode === 'create' ? '#185FA5' : '#888', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                  Create new squad
                </button>
              </div>
            )}

            {squadMode === 'join' && (
              <>
                <select style={inputStyle} value={selectedSquadId} onChange={e => setSelectedSquadId(e.target.value)}>
                  <option value="">Select a squad *</option>
                  {existingSquads.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {getSportLabel(s.sport)}{s.age_group ? ' · ' + s.age_group : ''}
                    </option>
                  ))}
                </select>
                <div style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#888' }}>
                  ⏳ Your request will be sent to the squad admin for approval.
                </div>
              </>
            )}

            {squadMode === 'create' && (
              <>
                <div style={{ background: '#EAF3DE', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: '#3B6D11' }}>
                  ✅ You'll become Squad Admin and can start adding players and fixtures straight away.
                </div>
                <select style={inputStyle} value={newSquadGender}
                  onChange={e => {
                    setNewSquadGender(e.target.value)
                    setNewSquadSport(e.target.value === 'girls' ? 'football' : 'football')
                  }}>
                  <option value="boys">Boys</option>
                  <option value="girls">Ladies</option>
                </select>
                <select style={inputStyle} value={newSquadSport}
                  onChange={e => setNewSquadSport(e.target.value)}>
                  {newSquadGender === 'girls' ? (
                    <>
                      <option value="football">Gaelic Football</option>
                      <option value="camogie">Camogie</option>
                      <option value="dual_ladies">Dual (Football & Camogie)</option>
                    </>
                  ) : (
                    <>
                      <option value="football">Gaelic Football</option>
                      <option value="hurling">Hurling</option>
                      <option value="dual">Dual (Football & Hurling)</option>
                    </>
                  )}
                </select>
                <select style={inputStyle} value={newSquadAgeGroup}
                  onChange={e => setNewSquadAgeGroup(e.target.value)}>
                  <option value="">Select age group *</option>
                  {AGE_GROUPS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {newSquadAgeGroup && (
                  <div style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '13px', color: '#333' }}>
                    Squad name: <strong>{newSquadAgeGroup} {newSquadGender === 'boys' ? 'Boys' : 'Ladies'}{publicUrlLabel.trim() ? ' - ' + publicUrlLabel.trim() : ''} · {getSportLabel(newSquadSport)}</strong>
                  </div>
                )}
                {collisionSquad && (
                  <div style={{ background: '#FFF6E5', border: '0.5px solid #C98A1B', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                    <p style={{ fontSize: '13px', color: '#7A5B10', lineHeight: '1.5', marginBottom: '10px' }}>
                      Your club already has a {newSquadAgeGroup} {newSquadGender === 'boys' ? 'Boys' : 'Ladies'} squad — <strong>{collisionSquad.name}</strong>. What should we call this one?
                    </p>
                    <input style={{ ...inputStyle, marginBottom: 0 }} type="text" placeholder={labelPlaceholder}
                      value={publicUrlLabel} onChange={e => setPublicUrlLabel(e.target.value)} />
                  </div>
                )}
              </>
            )}

            {squadMode && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...primaryBtn, background: '#888', flex: '0 0 80px' }}
                  onClick={() => { setStep(2); setError('') }}>← Back</button>
                <button style={{ ...primaryBtn, flex: 1 }} onClick={handleSignUpStep3}
                  disabled={loading || (squadMode === 'create' && collisionSquad && !publicUrlLabel.trim())}>
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
              </div>
            )}

            {!squadMode && (
              <button style={{ ...primaryBtn, background: '#888' }}
                onClick={() => { setStep(2); setError('') }}>← Back</button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function StepIndicator({ current, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
      {Array.from({ length: total }, (_, i) => i + 1).map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center', flex: i < total - 1 ? 1 : 'none' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: s <= current ? '#185FA5' : '#e0e0e0', color: s <= current ? 'white' : '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', flexShrink: 0 }}>
            {s}
          </div>
          {i < total - 1 && (
            <div style={{ flex: 1, height: '2px', background: s < current ? '#185FA5' : '#e0e0e0', margin: '0 4px' }} />
          )}
        </div>
      ))}
    </div>
  )
}

const shell = { minHeight: '100vh', background: 'linear-gradient(135deg, #185FA5 0%, #0d3d6b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }
const card = { background: 'white', borderRadius: '16px', padding: '28px 24px', width: '100%', maxWidth: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }
const inputStyle = { display: 'block', width: '100%', padding: '12px 14px', marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }
const primaryBtn = { display: 'block', width: '100%', padding: '13px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: 'pointer' }
const linkBtn = { background: 'none', border: 'none', color: '#185FA5', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }